'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PRETRIP_CONFIG, getPreTripFlipbooksForRegionSlug } from '@/features/stm/lib/pretrip-config'
import { PreTripVideoCard } from './PreTripVideoCard'
import { PreTripFlipbookCard } from './PreTripFlipbookCard'
import { PreTripErrorBoundary } from './PreTripErrorBoundary'
import { pdfFileToImages } from '@/features/stm/lib/pdf-to-images'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Info, Loader2 } from 'lucide-react'
import type { PreTripFlipbookFromApi, PreTripFromApi } from './GroupPreTripTab'
import type { PreTripFlipbookConfig } from '@/features/stm/lib/pretrip-config'
import {
  shouldUseVideoMultipartUpload,
  uploadBaseVideoMultipartChunks,
} from '@/features/photos/lib/upload-video-multipart'
import { authFetch, authFetchJson } from '@/shared/lib/auth-fetch'

async function fetchPreTrip(regionId: number): Promise<PreTripFromApi> {
  const res = await authFetchJson<PreTripFromApi>(`/base-photos/region/${regionId}/pretrip`)
  if (!res.success) {
    throw new Error(res.message || 'Failed to load PreTrip documents')
  }
  return res.data
}

function dataUrlToFile(dataUrl: string, name: string): Promise<File> {
  return fetch(dataUrl)
    .then((r) => r.blob())
    .then((blob) => new File([blob], name, { type: blob.type || 'application/octet-stream' }))
}

function flipbookHasContent(
  fb: PreTripFlipbookConfig,
  api: PreTripFlipbookFromApi | undefined
): boolean {
  const pageImageUrls = api?.pageImageUrls ?? fb.pageImageUrls
  const pdfDownloadUrl = api?.pdfDownloadUrl ?? fb.pdfDownloadUrl
  return (pageImageUrls?.length ?? 0) > 0 || !!(pdfDownloadUrl && String(pdfDownloadUrl).trim())
}

export type PreTripTabContentProps = {
  /** Region ID for loading/uploading PreTrip documents (same data as Volunteers Groups PreTrip). */
  regionId: number | null
  /** URL region slug (e.g. swannanoa-valley) — selects simple vs expanded PreTrip flipbook layout. */
  regionSlug?: string | null
  /** When true, show the Admin/Base Leader instructions card for adding documents. */
  showInstructionsCard?: boolean
  /** When true, Upload/Replace controls are enabled (Admin/Base Leader only). */
  canEditPreTrip?: boolean
  /** Optional slot indices to hide (omit to show the same flipbooks as Volunteers → Groups → PreTrip). */
  hiddenFlipbookSlotIndices?: number[]
  /** When true, on desktop the PreTrip Video card has a smaller height (Volunteer Groups PreTrip only). */
  compactVideoHeightOnDesktop?: boolean
}

/**
 * Shared PreTrip tab content: one video card + PDF flip book cards.
 * Used by Volunteers Group detail (PreTrip tab) and Hub (PreTrip tab).
 * Documents are stored in the region's Base Photos.
 */
export function PreTripTabContent({
  regionId,
  regionSlug = null,
  showInstructionsCard = false,
  canEditPreTrip = false,
  hiddenFlipbookSlotIndices,
  compactVideoHeightOnDesktop = false,
}: PreTripTabContentProps) {
  const queryClient = useQueryClient()
  const { video } = PRETRIP_CONFIG
  const flipbooks = useMemo(
    () => getPreTripFlipbooksForRegionSlug(regionSlug),
    [regionSlug]
  )
  const visibleFlipbooks = hiddenFlipbookSlotIndices?.length
    ? flipbooks.filter((fb) => !hiddenFlipbookSlotIndices.includes(fb.slotIndex))
    : flipbooks

  const { data: pretripData, isLoading } = useQuery({
    queryKey: ['pretrip', regionId],
    queryFn: () => fetchPreTrip(regionId!),
    enabled: regionId != null,
  })

  const [uploadingSlot, setUploadingSlot] = useState<number | null>(null)
  const [uploadingVideo, setUploadingVideo] = useState(false)
  const [uploadingPoster, setUploadingPoster] = useState(false)
  const [revertingPoster, setRevertingPoster] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const handleUpload = useCallback(
    async (slotIndex: number, file: File) => {
      if (regionId == null) {
        setUploadError('Region not selected.')
        return
      }
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        setUploadError('Please choose a PDF file.')
        return
      }
      const maxMb = 25
      if (file.size > maxMb * 1024 * 1024) {
        setUploadError(`PDF must be under ${maxMb} MB.`)
        return
      }
      setUploadError(null)
      setUploadingSlot(slotIndex)
      try {
        const result = await pdfFileToImages(file)

        const formData = new FormData()
        formData.append('regionId', String(regionId))
        formData.append('slotIndex', String(slotIndex))
        formData.append('pdf', file)
        for (let i = 0; i < result.pageImageUrls.length; i++) {
          const pageFile = await dataUrlToFile(result.pageImageUrls[i], `page-${i}.jpg`)
          formData.append('pages', pageFile)
        }

        const res = await authFetch('/base-photos/pretrip', {
          method: 'POST',
          body: formData,
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) {
          if (res.status === 413) {
            setUploadError(
              'Upload too large for the server (413). Try fewer pages or a smaller PDF; if this persists, contact support.'
            )
            return
          }
          setUploadError(json.message || json.error || res.statusText || 'Upload failed')
          return
        }
        if (!json.success) {
          setUploadError(json.message || 'Upload failed')
          return
        }
        await new Promise((r) => setTimeout(r, 0))
        await queryClient.refetchQueries({ queryKey: ['pretrip', regionId] })
        await new Promise((r) => setTimeout(r, 100))
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : 'Failed to process or upload PDF.')
      } finally {
        setUploadingSlot(null)
      }
    },
    [regionId, queryClient]
  )

  const handleVideoUpload = useCallback(
    async (file: File, options?: { keepCustomPoster?: boolean }) => {
      if (regionId == null) {
        setUploadError('Region not selected.')
        return
      }
      const videoTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v']
      if (!videoTypes.includes(file.type) && !/\.(mp4|mov|webm|m4v)$/i.test(file.name)) {
        setUploadError('Please choose a video file (MP4, MOV, WebM, or M4V).')
        return
      }
      const maxMb = 500
      if (file.size > maxMb * 1024 * 1024) {
        setUploadError(`Video must be under ${maxMb} MB.`)
        return
      }
      setUploadError(null)
      setUploadingVideo(true)
      try {
        if (shouldUseVideoMultipartUpload(file)) {
          await uploadBaseVideoMultipartChunks(
            regionId,
            file,
            {
              baseFinalizeKind: 'pretrip',
              keepCustomPoster: options?.keepCustomPoster,
            },
            undefined
          )
          await new Promise((r) => setTimeout(r, 0))
          await queryClient.refetchQueries({ queryKey: ['pretrip', regionId] })
          await new Promise((r) => setTimeout(r, 100))
          return
        }

        const formData = new FormData()
        formData.append('regionId', String(regionId))
        if (options?.keepCustomPoster) {
          formData.append('keepCustomPoster', 'true')
        }
        formData.append('video', file)
        const res = await authFetch('/base-photos/pretrip/video', {
          method: 'POST',
          body: formData,
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) {
          setUploadError(json.message || json.error || res.statusText || 'Upload failed')
          return
        }
        if (!json.success) {
          setUploadError(json.message || 'Upload failed')
          return
        }
        await new Promise((r) => setTimeout(r, 0))
        await queryClient.refetchQueries({ queryKey: ['pretrip', regionId] })
        await new Promise((r) => setTimeout(r, 100))
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : 'Failed to upload video.')
      } finally {
        setUploadingVideo(false)
      }
    },
    [regionId, queryClient]
  )

  const handlePosterUpload = useCallback(
    async (file: File) => {
      if (regionId == null) {
        setUploadError('Region not selected.')
        return
      }
      if (!file.type.startsWith('image/') && !/\.(jpe?g|png|gif|webp|bmp|tiff?|heic|heif|avif)$/i.test(file.name)) {
        setUploadError('Please choose an image file.')
        return
      }
      setUploadError(null)
      setUploadingPoster(true)
      try {
        const formData = new FormData()
        formData.append('regionId', String(regionId))
        formData.append('poster', file)
        const res = await authFetch('/base-photos/pretrip/poster', {
          method: 'POST',
          body: formData,
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) {
          setUploadError(json.message || json.error || res.statusText || 'Upload failed')
          return
        }
        if (!json.success) {
          setUploadError(json.message || 'Upload failed')
          return
        }
        await queryClient.refetchQueries({ queryKey: ['pretrip', regionId] })
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : 'Failed to upload poster.')
      } finally {
        setUploadingPoster(false)
      }
    },
    [regionId, queryClient]
  )

  const handleRevertPoster = useCallback(async () => {
    if (regionId == null) {
      setUploadError('Region not selected.')
      return
    }
    setUploadError(null)
    setRevertingPoster(true)
    try {
      const res = await authFetch('/base-photos/pretrip/poster/revert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regionId }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setUploadError(json.message || json.error || res.statusText || 'Could not reset poster')
        return
      }
      if (!json.success) {
        setUploadError(json.message || 'Could not reset poster')
        return
      }
      await queryClient.refetchQueries({ queryKey: ['pretrip', regionId] })
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Could not reset poster.')
    } finally {
      setRevertingPoster(false)
    }
  }, [regionId, queryClient])

  const apiFlipbooksBySlot = new Map<number, PreTripFlipbookFromApi>()
  if (pretripData?.flipbooks) {
    for (const fb of pretripData.flipbooks) {
      apiFlipbooksBySlot.set(fb.slotIndex, fb)
    }
  }

  /** Admin / Base Leader see every slot (including empty) so they can upload. Others only see slots that already have content. */
  const showEmptySlots = canEditPreTrip
  const hasVideoContent =
    Boolean(pretripData?.videoUrl?.trim()) ||
    Boolean(pretripData?.videoThumbnailUrl?.trim()) ||
    Boolean(video.url?.trim())
  const shouldShowVideoCard = showEmptySlots || (!isLoading && hasVideoContent)

  const hasAnyFlipbookContentForViewer = visibleFlipbooks.some((fb) =>
    flipbookHasContent(fb, apiFlipbooksBySlot.get(fb.slotIndex))
  )
  const showNoDocumentsMessage =
    !canEditPreTrip &&
    !isLoading &&
    regionId != null &&
    !hasVideoContent &&
    !hasAnyFlipbookContentForViewer

  const uploadingCardTitle =
    uploadingSlot !== null ? visibleFlipbooks.find((fb) => fb.slotIndex === uploadingSlot)?.title : null

  return (
    <PreTripErrorBoundary>
      <div className="space-y-6">
        {shouldShowVideoCard && (
        <PreTripVideoCard
          title={video.title}
          url={video.url}
          videoUrl={pretripData?.videoUrl ?? null}
          videoThumbnailUrl={pretripData?.videoThumbnailUrl ?? null}
          videoDuration={pretripData?.videoDuration ?? null}
          onUploadVideo={
            canEditPreTrip && !uploadingVideo && !uploadingPoster && !revertingPoster
              ? handleVideoUpload
              : undefined
          }
          isUploadingVideo={uploadingVideo}
          onUploadPoster={
            canEditPreTrip && pretripData?.videoUrl && !uploadingVideo && !uploadingPoster && !revertingPoster
              ? handlePosterUpload
              : undefined
          }
          isUploadingPoster={uploadingPoster}
          onRevertPoster={
            canEditPreTrip && pretripData?.videoUrl && !uploadingVideo && !uploadingPoster && !revertingPoster
              ? handleRevertPoster
              : undefined
          }
          isRevertingPoster={revertingPoster}
          isLoadingPreTrip={isLoading}
          compactHeightOnDesktop={compactVideoHeightOnDesktop}
        />
        )}

        {regionId == null && (
          <p className="text-sm text-muted-foreground">Loading region…</p>
        )}

        {uploadError && (
          <p className="text-sm text-destructive font-medium" role="alert">
            {uploadError}
          </p>
        )}

        {(uploadingSlot !== null || uploadingVideo || uploadingPoster || revertingPoster) && (
          <div
            className="fixed top-4 left-4 right-4 z-[100] mx-auto max-w-md md:top-6 md:left-1/2 md:right-auto md:-translate-x-1/2"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm font-medium text-foreground shadow-lg">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              <span>
                {uploadingVideo
                  ? 'Uploading video…'
                  : uploadingPoster
                    ? 'Uploading poster…'
                    : revertingPoster
                      ? 'Resetting poster…'
                      : `Uploading PDF${uploadingCardTitle ? ` for ${uploadingCardTitle}` : ''}…`}
              </span>
            </div>
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading documents…</p>
        ) : null}

        {showNoDocumentsMessage && (
          <p className="text-sm text-muted-foreground">
            No PreTrip documents are available for this region yet.
          </p>
        )}

        {visibleFlipbooks.map((fb, index) => {
          const slotIndex = fb.slotIndex
          const api = apiFlipbooksBySlot.get(slotIndex)
          if (!showEmptySlots && (isLoading || !flipbookHasContent(fb, api))) return null
          const pageImageUrls = api?.pageImageUrls ?? fb.pageImageUrls
          const pdfDownloadUrl = api?.pdfDownloadUrl ?? fb.pdfDownloadUrl
          const isUploading = uploadingSlot === slotIndex
          return (
            <PreTripFlipbookCard
              key={`${index}-${slotIndex}`}
              title={fb.title}
              pageImageUrls={pageImageUrls}
              pdfDownloadUrl={pdfDownloadUrl}
              onUpload={canEditPreTrip && !isUploading ? (file) => handleUpload(slotIndex, file) : undefined}
              isUploading={isUploading}
              infoTooltip={canEditPreTrip ? 'Upload a PDF to create a flip book. Group leaders and volunteers can download. Replace anytime by uploading a new PDF.' : undefined}
              isLoadingDocuments={isLoading}
            />
          )
        })}

        {showInstructionsCard && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <Info className="h-5 w-5 shrink-0 text-primary mt-0.5" />
                <div className="space-y-2 text-sm">
                  <p className="font-semibold text-foreground">How Admin & Base Leaders can add documents</p>
                  <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                    <li>
                      Click <strong className="text-foreground">&quot;Upload PDF&quot;</strong> on any
                      document card above.
                    </li>
                    <li>Choose a PDF file from your device.</li>
                    <li>
                      It will be converted into a flip book and saved to the base so volunteers can
                      view and download it.
                    </li>
                    <li>
                      Group leaders and volunteers can use{' '}
                      <strong className="text-foreground">&quot;Download PDF&quot;</strong> to save the
                      file.
                    </li>
                  </ol>
                  <p className="text-xs text-muted-foreground pt-1">
                    You can replace a document anytime by uploading a new PDF to that card. Documents
                    are stored in the region&apos;s Base Photos and are visible to everyone.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PreTripErrorBoundary>
  )
}
